import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  HiOutlineViewGrid,
  HiOutlineCash,
  HiOutlineLogout,
  HiOutlineMoon,
  HiOutlineSun,
  HiOutlineUserAdd,
  HiOutlineSupport,
  HiOutlineChevronDown,
  HiOutlineChartBar,
  HiOutlineChartSquareBar,
  HiOutlineUserGroup,
  HiOutlineStar,
  HiOutlineTrendingUp,
  HiOutlineLockClosed,
  HiMenuAlt3,
  HiX,
} from 'react-icons/hi';
import { TbWallet } from 'react-icons/tb';
import { useWallet, isWalletAddress } from '../context/WalletContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import { shortAddress } from '../services/wallet';
import ConnectModal from './ConnectModal';
import ConfirmModal from './ConfirmModal';
import './Layout.css';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: HiOutlineViewGrid, end: true },
  { to: '/plans', label: 'Plans', icon: HiOutlineCash },
  { to: '/staking', label: 'Staking', icon: HiOutlineLockClosed },
  { to: '/wallet', label: 'Wallet', icon: TbWallet },
  { to: '/income', label: 'Income', icon: HiOutlineChartBar },
  { to: '/commission', label: 'Commission', icon: HiOutlineChartSquareBar },
  { to: '/level', label: 'Level', icon: HiOutlineTrendingUp },
  { to: '/team', label: 'Team', icon: HiOutlineUserGroup },
  { to: '/rank', label: 'Rank', icon: HiOutlineStar },
  { to: '/invite', label: 'Invite', icon: HiOutlineUserAdd },
  { to: '/support', label: 'Support', icon: HiOutlineSupport },
];

function inviteFromPath(pathname) {
  const match = pathname.match(/^\/dashboard\/(0x[a-fA-F0-9]{40})\/?$/i);
  return match ? match[1] : '';
}

export default function Layout() {
  const { user, openConnect, connectOpening, disconnect, address, setReferralUpline } = useWallet();
  const { isDark, toggleTheme } = useTheme();
  const toast = useToast();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [addrMenuOpen, setAddrMenuOpen] = useState(false);
  const addrMenuRef = useRef(null);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('dynafi_sidebar_collapsed') === '1';
    } catch {
      return false;
    }
  });

  useLayoutEffect(() => {
    const invite = inviteFromPath(location.pathname);
    if (invite && isWalletAddress(invite)) {
      setReferralUpline(invite);
    }
  }, [location.pathname, setReferralUpline]);

  useEffect(() => {
    try {
      localStorage.setItem('dynafi_sidebar_collapsed', collapsed ? '1' : '0');
    } catch {
      // ignore
    }
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
    setAddrMenuOpen(false);
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      const main = document.querySelector('.shell__main');
      if (main) main.scrollTop = 0;
      const content = document.querySelector('.shell__content');
      if (content) content.scrollTop = 0;
    } catch {
      // ignore
    }
  }, [location.pathname]);

  useEffect(() => {
    function onDocClick(e) {
      if (!addrMenuRef.current?.contains(e.target)) {
        setAddrMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function toggleSidebar() {
    if (window.innerWidth <= 900) {
      setMobileOpen((v) => !v);
    } else {
      setCollapsed((v) => !v);
    }
  }

  function requestDisconnect() {
    setAddrMenuOpen(false);
    setDisconnectOpen(true);
  }

  function confirmDisconnect() {
    disconnect();
    setDisconnectOpen(false);
    toast.success('Wallet disconnected');
  }

  const shellClass = [
    'shell',
    collapsed ? 'shell--collapsed' : '',
    mobileOpen ? 'shell--nav-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className={shellClass}>
        <aside className="shell__sidebar">
          <div className="shell__side-top">
            <Link to="/dashboard" className="shell__brand" onClick={() => setMobileOpen(false)}>
              <img className="shell__logo" src="/logo-64.png" alt="" />
              <div className="shell__brand-text">
                <strong>Dynamic Future Defi</strong>
                <span>DeFi Platform</span>
              </div>
            </Link>
          </div>

          <nav className="shell__nav">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={!!item.end}
                  title={item.label}
                  onClick={() => setMobileOpen(false)}
                >
                  <Icon className="shell__nav-icon" />
                  <span className="shell__nav-label">{item.label}</span>
                </NavLink>
              );
            })}
          </nav>

          <div className="shell__side-foot">
            {user ? (
              <>
                <div className="shell__addr" title={address}>
                  {collapsed ? shortAddress(address).slice(0, 4) : shortAddress(address)}
                </div>
                <button
                  className="btn btn--ghost btn--block"
                  type="button"
                  onClick={requestDisconnect}
                  title="Disconnect"
                >
                  <HiOutlineLogout />
                  <span className="shell__nav-label">Disconnect</span>
                </button>
              </>
            ) : (
              <button
                className={`btn btn--primary btn--block${connectOpening ? ' is-busy' : ''}`}
                type="button"
                onClick={() => openConnect('Connect to access your account')}
                title="Connect"
                disabled={connectOpening}
                aria-busy={connectOpening}
              >
                {connectOpening ? <span className="btn__spin" aria-hidden /> : <TbWallet />}
                <span className="shell__nav-label">{connectOpening ? 'Connecting…' : 'Connect'}</span>
              </button>
            )}
          </div>
        </aside>

        <div className="shell__content">
          <header className="shell__topbar">
            <div className="shell__topbar-left">
              <motion.button
                className="menu-toggle"
                type="button"
                onClick={toggleSidebar}
                aria-label="Toggle sidebar"
                whileTap={{ scale: 0.92 }}
              >
                {mobileOpen ? <HiX size={22} /> : <HiMenuAlt3 size={22} />}
              </motion.button>
              <Link to="/dashboard" className="shell__top-brand">
                <img src="/logo-64.png" alt="" />
                <strong>Dynamic Future Defi</strong>
              </Link>
            </div>
            <div className="shell__actions">
              <button
                className="theme-toggle"
                type="button"
                onClick={toggleTheme}
                aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
                title={isDark ? 'Light mode' : 'Dark mode'}
              >
                {isDark ? <HiOutlineSun size={18} /> : <HiOutlineMoon size={18} />}
              </button>
              {user ? (
                <div className="shell__addr-menu" ref={addrMenuRef}>
                  <button
                    className="shell__addr-btn"
                    type="button"
                    onClick={() => setAddrMenuOpen((v) => !v)}
                    aria-expanded={addrMenuOpen}
                    title={address}
                  >
                    {shortAddress(address)}
                    <HiOutlineChevronDown size={14} />
                  </button>
                  <AnimatePresence>
                    {addrMenuOpen ? (
                      <motion.div
                        className="shell__addr-dropdown"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                      >
                        <button type="button" onClick={requestDisconnect}>
                          <HiOutlineLogout /> Disconnect
                        </button>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              ) : (
                <motion.button
                  className={`btn btn--primary${connectOpening ? ' is-busy' : ''}`}
                  type="button"
                  onClick={() => openConnect('Connect to access your account')}
                  whileHover={connectOpening ? undefined : { scale: 1.03 }}
                  whileTap={connectOpening ? undefined : { scale: 0.97 }}
                  disabled={connectOpening}
                  aria-busy={connectOpening}
                >
                  {connectOpening ? <span className="btn__spin" aria-hidden /> : <TbWallet />}
                  {connectOpening ? 'Connecting…' : 'Connect'}
                </motion.button>
              )}
            </div>
          </header>
          <main className="shell__main">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen ? (
          <motion.div
            className="shell__backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileOpen(false)}
            role="presentation"
          />
        ) : null}
      </AnimatePresence>

      <ConnectModal />

      <ConfirmModal
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        onConfirm={confirmDisconnect}
        title="Disconnect wallet?"
        subtitle="You can connect again anytime from the navbar."
        confirmLabel="Disconnect"
        danger
      >
        <p className="muted" style={{ margin: 0 }}>
          This signs you out of Dynamic Future Defi on this device. Your on-chain funds stay in your wallet.
        </p>
      </ConfirmModal>
    </>
  );
}
